import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { color } from '../../../app/theme';
import { ConditionsKeywordType } from '../../../app/types';
import {
  addKeywordConditionsNode,
  setNodeById,
  setSelectedNode,
} from '../../../features/mainSlice';
import AutoCompleted from '../../shared/AutoCompleted';
import Quota from '../../shared/Quota';
import RowTitle from '../../shared/RowTitle';
import Tag from '../../shared/Tag';

const MAX_KEYWORD_COUNT = 10;

const useStyles = makeStyles(
  {
    root: {
      marginTop: 16,
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: 8,
    },
    targetInput: {
      width: '100%',
    },
    tag: {
      marginBottom: 8,
    },
    keywordLabel: {
      fontSize: '15px',
      fontWeight: "'400'",
      lineHeight: 1,
      color: color.$13,
    },
    radioInput: {
      fontSize: 15,
    },
    block: {
      padding: '12px 16px',
      borderRadius: 24,
      backgroundColor: 'rgba(224, 224, 224, 0.2)',
    },
  },
  {
    name: 'Keyword',
  },
);

const Keyword = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { selectedNode, isErrorNodeIds } = useSelector(state => state.ma);

  const groupId = selectedNode?.id;
  const { keywords, keywordType } = selectedNode?.data;

  const isError = !!~isErrorNodeIds.indexOf(groupId);
  const validKeywords = keywords?.filter(k => k?.trim()) || [];
  const isMaxReached = validKeywords.length >= MAX_KEYWORD_COUNT;

  const onKeywordTypeChange = value => {
    const source = selectedNode?.data?.source;

    // 先更新節點資料
    const newNode = {
      ...selectedNode,
      data: {
        ...selectedNode.data,
        keywordType: value,
        keywords: [],
      },
    };
    dispatch(setSelectedNode(newNode));
    dispatch(setNodeById(newNode));

    // 延遲一下再重新構建分支結構，確保 store 更新完成
    setTimeout(() => {
      dispatch(addKeywordConditionsNode(source, groupId));
    }, 0);
  };

  const onRemoveKeyword = index => {
    const newNode = {
      ...selectedNode,
      data: {
        ...selectedNode.data,
        keywords: keywords.filter((_, i) => i !== index),
      },
    };
    dispatch(setSelectedNode(newNode));
    dispatch(setNodeById(newNode));
  };

  const onAddKeyword = value => {
    const trimmedValue = value?.trim();
    if (!trimmedValue) return;
    if (validKeywords.length >= MAX_KEYWORD_COUNT) return;

    const newNode = {
      ...selectedNode,
      data: {
        ...selectedNode.data,
        keywords: [...keywords, trimmedValue],
      },
    };
    dispatch(setSelectedNode(newNode));
    dispatch(setNodeById(newNode));
  };

  return (
    <div className={classes.root}>
      <RowTitle
        title={t('condition-setting.keywordType')}
        subtitle={t('condition-setting.keywordTypeSubtitle')}
        isRequired
      />
      <div style={{ marginBottom: 12 }}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="keyword-display"
            name="keyword-display"
            value={keywordType}
            onChange={e => {
              e.stopPropagation();
              onKeywordTypeChange(e?.target?.value);
            }}
          >
            <FormControlLabel
              className={classes.radioInput}
              value={ConditionsKeywordType.ANY}
              control={<Radio color="primary" />}
              label={
                <div className={classes.keywordLabel}>
                  {t('condition-setting.specificAny')}
                </div>
              }
            />
            <FormControlLabel
              className={classes.radioInput}
              value={ConditionsKeywordType.KEYWORD}
              control={<Radio color="primary" />}
              label={
                <div className={classes.keywordLabel}>
                  {t('condition-setting.specificKeyword')}
                </div>
              }
            />
          </RadioGroup>
        </FormControl>
      </div>
      {keywordType === ConditionsKeywordType.KEYWORD && (
        <div className={classes.block}>
          <RowTitle
            title={t('condition-setting.keywords')}
            subtitle={t('condition-setting.keywordsSubtitle')}
            isRequired
          />
          <Quota
            style={{ marginBottom: 8 }}
            usedText={t('condition-setting.usedText')}
            quotaText={t('condition-setting.quotaText')}
            used={validKeywords.length}
            quota={MAX_KEYWORD_COUNT}
          />
          <div className={classes.row}>
            <AutoCompleted
              className={classes.targetInput}
              placeholder={t('condition-setting.keywordPlaceholder')}
              candidates={[]}
              onFocus={() => {}}
              onChange={() => {}}
              onEnter={value => onAddKeyword(value)}
              isError={isError && !validKeywords.length}
              disabled={isMaxReached}
            />
          </div>
          <div>
            {keywords.map((keyword, idx) => (
              <Tag
                key={`keyword-${idx}`}
                className={classes.tag}
                size="M"
                tag={keyword}
                clickable
                conditionIndex={0}
                idx={idx}
                color="colorPrimary"
                onDeleteFunction={onRemoveKeyword}
                deleteable
                isCondition
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Keyword;
